import { useKeycloak } from "@react-keycloak/web";
import { JSX, useEffect, useState } from "react";

/**
 * A Route that checks if the user is logged in and renders the `children`
 * component if they are. Otherwise, it handles authentication flow.
 *
 * Token refresh is handled centrally by SessionNetworkManager.
 * PrivateRoute only checks authentication state and redirects if needed.
 *
 * @param children The component to render if the user is logged in.
 * @returns The rendered component.
 */
const PrivateRoute = ({ children }:{children: JSX.Element}) => {
  const { keycloak, initialized } = useKeycloak();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hashData = (location.hash || "").replace(/^#/, "");
  const hashParams = new URLSearchParams(hashData);

  const isLoggedIn = keycloak.authenticated;

  useEffect(() => {
    if (!initialized) {
      console.log('🔐 Keycloak not initialized yet...');
      return;
    }

    console.log('🔐 PrivateRoute: Keycloak initialized, authenticated =', keycloak.authenticated);
    setIsCheckingAuth(false);

    // Token refresh is handled by SessionNetworkManager — no need to refresh here.
    // PrivateRoute only gates access based on current auth state.
  }, [keycloak, initialized]);

  // Show loading while Keycloak is initializing
  if (!initialized || isCheckingAuth) {
    console.log('🔐 Keycloak initializing...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#171717] mx-auto mb-4"></div>
          <p className="text-[#555]">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated and no session_state in hash
  if (keycloak && !isLoggedIn && !hashParams.has('session_state')) {
    console.log('🔐 User not authenticated, redirecting to login...');
    
    try {
      keycloak.login({
        redirectUri: window.location.href
      });
    } catch (error) {
      console.error('❌ Login failed:', error);
      // Don't call init() again - it's already initialized
      // Just redirect to login page
      window.location.href = '/';
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#171717] mx-auto mb-4"></div>
          <p className="text-[#555]">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // If there's session_state in hash, we're returning from authorization
  if (hashParams.has('session_state') && !isLoggedIn) {
    console.log('🔐 Processing login callback...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#171717] mx-auto mb-4"></div>
          <p className="text-[#555]">Processing login...</p>
        </div>
      </div>
    );
  }

  console.log('✅ User authenticated, rendering protected component');
  return children;
};

export default PrivateRoute;
